import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSEO } from '../lib/useSEO';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { boardsAPI, communityAPI } from '../lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  CheckCircle2, XCircle, Clock, Trophy, ArrowLeft,
  RotateCcw, BarChart3, Target, Star, SkipForward, ChevronLeft, ChevronRight, Share2, Crown, Trash2, Link2
} from 'lucide-react';

const ROUND_TIME = 600;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';
const CLUE_TIME = 20;
const FINAL_CLUE_TIME = 25;

const ROUND_CONFIG = {
  1: { values: [100, 200, 300, 400, 500], ddCount: 1, label: 'Round 1' },
  2: { values: [200, 400, 600, 800, 1000], ddCount: 2, label: 'Round 2' },
};

function pickDailyDoubles(categories, count) {
  const allPositions = [];
  categories.forEach((_, ci) => {
    for (let ri = 1; ri < 5; ri++) allPositions.push({ cat: ci, row: ri });
  });
  for (let i = allPositions.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allPositions[i], allPositions[j]] = [allPositions[j], allPositions[i]];
  }
  return allPositions.slice(0, count);
}

// Reusable stats display
function StatsDisplay({ answeredClues, score, title, subtitle, onContinue, continueLabel, onPlayAgain, onDashboard, onShareCommunity, shareStatus, challengeResult, shareLink }) {
  const correct = answeredClues.filter(r => r.is_correct).length;
  const incorrect = answeredClues.filter(r => !r.is_correct && !r.was_skipped).length;
  const skipped = answeredClues.filter(r => r.was_skipped).length;
  const answered = correct + incorrect;
  const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;
  const missed = incorrect + skipped;

  const categoryMap = {};
  answeredClues.filter(r => !r.isFinal).forEach(r => {
    if (!categoryMap[r.category]) categoryMap[r.category] = { correct: 0, incorrect: 0, skipped: 0 };
    if (r.was_skipped) categoryMap[r.category].skipped++;
    else if (r.is_correct) categoryMap[r.category].correct++;
    else categoryMap[r.category].incorrect++;
  });

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-3 sm:p-4">
      <div className="w-full max-w-2xl">
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-4 sm:p-8">
            <div className="text-center mb-6 sm:mb-8">
              <Trophy className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[hsl(var(--brand-gold-500))] mb-3 sm:mb-4" />
              <h1 className="font-display font-bold text-2xl sm:text-3xl text-white mb-2">{title}</h1>
              {subtitle && <p className="text-gray-400 text-sm sm:text-base truncate">{subtitle}</p>}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mb-6 sm:mb-8">
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 sm:p-4 text-center">
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1 text-[hsl(var(--brand-gold-500))]" />
                <p className={`text-lg sm:text-2xl font-bold ${score >= 0 ? 'text-[hsl(var(--brand-gold-500))]' : 'text-rose-400'}`} data-testid="stats-score">${score.toLocaleString()}</p>
                <p className="text-gray-400 text-xs">Score</p>
              </div>
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 sm:p-4 text-center">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1 text-emerald-400" />
                <p className={`text-lg sm:text-2xl font-bold ${accuracy >= 60 ? 'text-emerald-400' : accuracy >= 40 ? 'text-yellow-400' : 'text-rose-400'}`} data-testid="stats-accuracy">{accuracy}%</p>
                <p className="text-gray-400 text-xs">Accuracy</p>
              </div>
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 sm:p-4 text-center">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1 text-emerald-400" />
                <p className="text-lg sm:text-2xl font-bold text-emerald-400" data-testid="stats-correct">{correct}</p>
                <p className="text-gray-400 text-xs">Correct</p>
              </div>
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 sm:p-4 text-center">
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1 text-rose-400" />
                <p className="text-lg sm:text-2xl font-bold text-rose-400" data-testid="stats-missed">{missed}</p>
                <p className="text-gray-400 text-xs">Missed</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
              <div className="bg-emerald-500/10 rounded-lg p-2 sm:p-3 text-center">
                <p className="text-emerald-400 text-xs sm:text-sm font-medium">Correct</p>
                <p className="text-white text-lg sm:text-xl font-bold">{correct}</p>
              </div>
              <div className="bg-rose-500/10 rounded-lg p-2 sm:p-3 text-center">
                <p className="text-rose-400 text-xs sm:text-sm font-medium">Incorrect</p>
                <p className="text-white text-lg sm:text-xl font-bold">{incorrect}</p>
              </div>
              <div className="bg-orange-500/10 rounded-lg p-2 sm:p-3 text-center">
                <p className="text-orange-400 text-xs sm:text-sm font-medium">Skipped</p>
                <p className="text-white text-lg sm:text-xl font-bold">{skipped}</p>
              </div>
            </div>

            {Object.keys(categoryMap).length > 0 && (
              <div className="mb-6 sm:mb-8">
                <h3 className="text-white font-semibold mb-3 text-sm sm:text-base">Category Breakdown</h3>
                <div className="space-y-2">
                  {Object.entries(categoryMap).map(([cat, data]) => {
                    const catAnswered = data.correct + data.incorrect;
                    const catAcc = catAnswered > 0 ? Math.round((data.correct / catAnswered) * 100) : 0;
                    return (
                      <div key={cat} className="flex items-center gap-2 sm:gap-3">
                        <span className="text-gray-300 text-xs sm:text-sm w-20 sm:w-40 truncate shrink-0">{cat}</span>
                        <div className="flex-1 h-2 bg-[hsl(var(--accent))] rounded-full overflow-hidden min-w-0">
                          <div className="h-full rounded-full" style={{
                            width: `${catAcc}%`,
                            backgroundColor: catAcc >= 60 ? '#34d399' : catAcc >= 40 ? '#fbbf24' : '#fb7185',
                          }} />
                        </div>
                        <span className={`text-xs sm:text-sm font-mono w-10 sm:w-12 text-right shrink-0 ${catAcc >= 60 ? 'text-emerald-400' : catAcc >= 40 ? 'text-yellow-400' : 'text-rose-400'}`}>{catAcc}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {onContinue && (
                <Button onClick={onContinue} className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-6 py-5" data-testid="continue-btn">
                  {continueLabel || 'Continue'}<ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              )}
              {onShareCommunity && (
                <Button onClick={onShareCommunity} disabled={shareStatus === 'shared'} className="bg-emerald-600 text-white hover:bg-emerald-700 px-6 py-5" data-testid="share-community-btn">
                  <Share2 className="w-5 h-5 mr-2" />
                  {shareStatus === 'sharing' ? 'Sharing...' : shareStatus === 'shared' ? 'Shared!' : 'Share to Community'}
                </Button>
              )}
              {challengeResult && (
                <div className={`w-full text-center p-4 rounded-lg mb-2 ${challengeResult.new_leader ? 'bg-emerald-500/20 border border-emerald-500/40' : 'bg-[hsl(var(--brand-blue-800))]'}`}>
                  {challengeResult.new_leader ? (
                    <>
                      <Crown className="w-8 h-8 mx-auto text-[hsl(var(--brand-gold-500))] mb-2" />
                      <p className="text-emerald-400 font-bold text-lg">New Leader!</p>
                      <p className="text-gray-400 text-sm">You beat the previous score of ${challengeResult.previous_leader_score?.toLocaleString()}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-white font-bold text-lg">Score: ${challengeResult.your_score?.toLocaleString()}</p>
                      <p className="text-gray-400 text-sm">Leader's score: ${challengeResult.previous_leader_score?.toLocaleString()}</p>
                    </>
                  )}
                </div>
              )}
              {shareLink && (
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(shareLink).then(() => {
                      toast.success('Challenge link copied! Share it with friends.');
                    }).catch(() => {
                      toast.error('Failed to copy link');
                    });
                  }}
                  variant="outline"
                  className="border-[hsl(var(--brand-gold-500))]/40 text-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-gold-500))]/10 px-6 py-5"
                  data-testid="copy-challenge-link-btn"
                >
                  <Link2 className="w-5 h-5 mr-2" />Copy Challenge Link
                </Button>
              )}
              {onPlayAgain && (
                <Button onClick={onPlayAgain} className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-6 py-5" data-testid="play-again-btn">
                  <RotateCcw className="w-5 h-5 mr-2" />Play Again
                </Button>
              )}
              {onDashboard && (
                <Button onClick={onDashboard} variant="outline" className="border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--accent))] px-6 py-5" data-testid="back-dashboard-btn">
                  <ArrowLeft className="w-5 h-5 mr-2" />Dashboard
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function BoardPlay() {
  const { boardId } = useParams();
  useSEO('Play Board | JepPrep', 'Play a full Jeopardy-style trivia board with three rounds of escalating difficulty.');
  const navigate = useNavigate();
  const location = useLocation();

  const isCommunityChallenge = location.state?.communityChallenge || location.pathname.startsWith('/community/');

  const [board, setBoard] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [currentRound, setCurrentRound] = useState(1); // 1, 2, or 3
  const [shareStatus, setShareStatus] = useState(null); // null, 'sharing', 'shared'
  const [challengeResult, setChallengeResult] = useState(null);

  const [revealed, setRevealed] = useState({});
  const [dailyDoubles, setDailyDoubles] = useState([]);
  const [score, setScore] = useState(0);
  const [allClues, setAllClues] = useState([]); // all rounds combined
  const [roundClues, setRoundClues] = useState([]); // current round only
  const [selectedClue, setSelectedClue] = useState(null);
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [wager, setWager] = useState('');
  const [roundTimeLeft, setRoundTimeLeft] = useState(ROUND_TIME);
  const [clueTimeLeft, setClueTimeLeft] = useState(CLUE_TIME);
  const [isLastQuestion, setIsLastQuestion] = useState(false);
  const [currentCategories, setCurrentCategories] = useState([]);
  const [round2Ready, setRound2Ready] = useState(false);
  const [round2Loading, setRound2Loading] = useState(false);
  const [mobileCatIdx, setMobileCatIdx] = useState(null);
  const [boardExpired, setBoardExpired] = useState(false);

  const inputRef = useRef(null);
  const touchStartRef = useRef(null);
  const roundTimerRef = useRef(null);
  const clueTimerRef = useRef(null);
  const isSubmittingRef = useRef(false);

  useEffect(() => { isSubmittingRef.current = isSubmitting; }, [isSubmitting]);

  // Trigger Round 2 generation when Round 1 stats are shown
  useEffect(() => {
    if (phase === 'round_stats' && currentRound === 1 && !round2Ready && !round2Loading) {
      if (isCommunityChallenge) {
        setRound2Ready(true);
      } else {
        triggerRound2Generation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentRound]);

  useEffect(() => {
    loadBoard();
    return () => { clearInterval(roundTimerRef.current); clearInterval(clueTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  const loadBoard = async () => {
    try {
      let boardData;
      if (location.state?.board) {
        boardData = location.state.board;
      } else if (isCommunityChallenge) {
        // Refresh scenario: fetch via community endpoint
        boardData = (await communityAPI.getBoard(boardId)).data;
      } else {
        boardData = (await boardsAPI.get(boardId)).data;
      }

      // Check play count for own boards (not community challenges)
      if (!isCommunityChallenge && boardData.play_count >= 3) {
        setBoard(boardData);
        setBoardExpired(true);
        return;
      }

      setBoard(boardData);
      setupRound(1, boardData);
    } catch (error) {
      toast.error('Failed to load board');
      navigate(isCommunityChallenge ? '/community' : '/dashboard');
    }
  };

  const setupRound = (round, boardData) => {
    const bd = boardData || board;
    let cats;
    if (round === 1) {
      cats = bd.categories || [];
    } else if (round === 2) {
      cats = bd.round2_categories || bd.categories || [];
    }
    setCurrentCategories(cats);
    setRevealed({});
    setRoundClues([]);
    setIsLastQuestion(false);
    setMobileCatIdx(null);

    const config = ROUND_CONFIG[round];
    setDailyDoubles(pickDailyDoubles(cats, config.ddCount));
    setCurrentRound(round);
    setPhase('board');
    startRoundTimer();
  };

  const startRoundTimer = () => {
    setRoundTimeLeft(ROUND_TIME);
    setIsLastQuestion(false);
    if (roundTimerRef.current) clearInterval(roundTimerRef.current);
    roundTimerRef.current = setInterval(() => {
      setRoundTimeLeft(prev => {
        if (prev <= 1) { clearInterval(roundTimerRef.current); setIsLastQuestion(true); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const startClueTimer = useCallback((duration) => {
    const t = duration || CLUE_TIME;
    if (clueTimerRef.current) clearInterval(clueTimerRef.current);
    setClueTimeLeft(t);
    clueTimerRef.current = setInterval(() => {
      setClueTimeLeft(prev => {
        if (prev <= 1) { clearInterval(clueTimerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-submit on clue timeout
  useEffect(() => {
    if (clueTimeLeft === 0 && !isSubmittingRef.current) {
      if (phase === 'dd_wager') handleDDWagerSubmit(0);
      else if (phase === 'clue') handleSubmitAnswer('', true);
      else if (phase === 'final_clue') submitFinalAnswer('', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clueTimeLeft, phase]);

  const isDailyDouble = (catIdx, rowIdx) => dailyDoubles.some(dd => dd.cat === catIdx && dd.row === rowIdx);
  const totalClues = currentCategories.reduce((sum, c) => sum + (c.clues?.length || 0), 0);
  const answeredCount = Object.keys(revealed).length;
  const roundConfig = ROUND_CONFIG[currentRound] || ROUND_CONFIG[1];

  const selectClue = (catIdx, rowIdx) => {
    if (revealed[`${catIdx}-${rowIdx}`] || phase !== 'board') return;
    const cat = currentCategories[catIdx];
    const clue = cat.clues[rowIdx];
    const clueData = { catIdx, rowIdx, category: cat.name, value: roundConfig.values[rowIdx], question: clue.question, correctAnswer: clue.answer };
    setSelectedClue(clueData);
    setAnswer('');

    if (isDailyDouble(catIdx, rowIdx)) {
      setPhase('dd_reveal');
      clearInterval(clueTimerRef.current);
      setTimeout(() => { setPhase('dd_wager'); setWager(''); startClueTimer(); setTimeout(() => inputRef.current?.focus(), 100); }, 1500);
    } else {
      setPhase('clue');
      startClueTimer();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleDDWagerSubmit = (forcedWager) => {
    const wagerAmount = forcedWager !== undefined ? forcedWager : Math.max(0, parseInt(wager) || 0);
    const maxWager = Math.max(score, currentRound === 1 ? 500 : 1000);
    setSelectedClue(prev => ({ ...prev, wager: Math.min(wagerAmount, maxWager) }));
    setPhase('clue');
    setAnswer('');
    startClueTimer();
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSkip = () => {
    if (isSubmittingRef.current) return;
    clearInterval(clueTimerRef.current);
    setRevealed(prev => ({ ...prev, [`${selectedClue.catIdx}-${selectedClue.rowIdx}`]: true }));
    const resultData = {
      is_correct: false, was_skipped: true, was_timeout: false,
      player_answer: '', correct_answer: selectedClue.correctAnswer,
      category: selectedClue.category, value: selectedClue.value,
      wager: selectedClue.wager, score_change: 0, round: currentRound,
    };
    setRoundClues(prev => [...prev, resultData]);
    setAllClues(prev => [...prev, resultData]);
    setLastResult(resultData);
    setPhase('skip_feedback');

    setTimeout(() => {
      setLastResult(null);
      const newAnswered = answeredCount + 1;
      if (newAnswered >= totalClues || isLastQuestion) {
        clearInterval(roundTimerRef.current);
        setPhase('round_stats');
      } else {
        setPhase('board');
      }
    }, 1200);
  };

  const handleSubmitAnswer = async (ans, isTimeout = false) => {
    if (isSubmittingRef.current) return;
    setIsSubmitting(true);
    isSubmittingRef.current = true;
    clearInterval(clueTimerRef.current);
    const answerText = ans !== undefined ? ans : answer;

    try {
      const response = await boardsAPI.soloAnswer(boardId, { answer: answerText || '', correct_answer: selectedClue.correctAnswer });
      const isCorrect = response.data.is_correct;
      const clueValue = selectedClue.wager !== undefined ? selectedClue.wager : selectedClue.value;
      const scoreChange = isCorrect ? clueValue : -clueValue;

      setScore(prev => prev + scoreChange);
      setRevealed(prev => ({ ...prev, [`${selectedClue.catIdx}-${selectedClue.rowIdx}`]: true }));

      const resultData = {
        is_correct: isCorrect, was_skipped: false, was_timeout: isTimeout,
        player_answer: answerText || '', correct_answer: selectedClue.correctAnswer,
        category: selectedClue.category, value: selectedClue.value,
        wager: selectedClue.wager, score_change: scoreChange, round: currentRound,
      };
      setLastResult(resultData);
      setRoundClues(prev => [...prev, resultData]);
      setAllClues(prev => [...prev, resultData]);
      setPhase('result_feedback');

      setTimeout(() => {
        setLastResult(null);
        setIsSubmitting(false);
        isSubmittingRef.current = false;
        const newAnswered = answeredCount + 1;
        if (newAnswered >= totalClues || isLastQuestion) {
          clearInterval(roundTimerRef.current);
          setPhase('round_stats');
        } else {
          setPhase('board');
        }
      }, 2000);
    } catch (error) {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      toast.error('Connection issue');
      setPhase('board');
    }
  };

  const handleFormSubmit = (e) => { e.preventDefault(); handleSubmitAnswer(answer); };

  // Trigger Round 2 generation in background
  const triggerRound2Generation = async () => {
    if (round2Ready || round2Loading) return;
    setRound2Loading(true);
    try {
      const response = await boardsAPI.generateRound2(boardId);
      if (response.data.status === 'ready') {
        setBoard(prev => ({ ...prev, round2_categories: response.data.round2_categories }));
        setRound2Ready(true);
        setRound2Loading(false);
      } else if (response.data.status === 'generating') {
        // Poll until ready
        const poll = setInterval(async () => {
          try {
            const r = await boardsAPI.generateRound2(boardId);
            if (r.data.status === 'ready') {
              clearInterval(poll);
              setBoard(prev => ({ ...prev, round2_categories: r.data.round2_categories }));
              setRound2Ready(true);
              setRound2Loading(false);
            }
          } catch { clearInterval(poll); setRound2Loading(false); }
        }, 3000);
      }
    } catch (error) {
      console.error('Round 2 generation failed:', error);
      setRound2Loading(false);
    }
  };

  // After round stats, proceed to next round
  const proceedFromRoundStats = () => {
    if (currentRound === 1) {
      const bd = board;
      const r2cats = bd.round2_categories;
      if (r2cats && r2cats.length > 0) {
        setupRound(2, bd);
      } else if (round2Loading) {
        // Still generating — show brief loading then proceed
        toast.info('Preparing Round 2...');
        const waitInterval = setInterval(() => {
          if (round2Ready) {
            clearInterval(waitInterval);
            setupRound(2, null);
          }
        }, 500);
      } else {
        // Fallback: use same categories
        setupRound(2, bd);
      }
    } else if (currentRound === 2) {
      if (board.final_jeopardy) {
        setCurrentRound(3);
        setPhase('final_wager');
        setWager('');
        setAnswer('');
      } else {
        setPhase('final_results');
      }
    }
  };

  const submitFinalWager = () => {
    const wagerAmount = Math.max(0, parseInt(wager) || 0);
    const maxWager = Math.max(score, 0);
    setSelectedClue({
      category: board.final_jeopardy.category,
      question: board.final_jeopardy.question,
      correctAnswer: board.final_jeopardy.answer,
      value: 0, wager: Math.min(wagerAmount, maxWager), isFinal: true,
    });
    setPhase('final_clue');
    setAnswer('');
    startClueTimer(FINAL_CLUE_TIME);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const submitFinalAnswer = async (ans, isTimeout = false) => {
    if (isSubmittingRef.current) return;
    setIsSubmitting(true);
    isSubmittingRef.current = true;
    clearInterval(clueTimerRef.current);
    const answerText = ans !== undefined ? ans : answer;

    try {
      const response = await boardsAPI.soloAnswer(boardId, { answer: answerText || '', correct_answer: selectedClue.correctAnswer });
      const isCorrect = response.data.is_correct;
      const scoreChange = isCorrect ? selectedClue.wager : -selectedClue.wager;
      setScore(prev => prev + scoreChange);

      const resultData = {
        is_correct: isCorrect, was_skipped: false, was_timeout: isTimeout,
        player_answer: answerText || '', correct_answer: selectedClue.correctAnswer,
        category: selectedClue.category, value: 0,
        wager: selectedClue.wager, score_change: scoreChange, isFinal: true, round: 3,
      };
      setLastResult(resultData);
      setAllClues(prev => [...prev, resultData]);
      setPhase('final_feedback');

      setTimeout(() => {
        setIsSubmitting(false);
        isSubmittingRef.current = false;
        setPhase('final_results');
      }, 2500);
    } catch (error) {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      setPhase('final_results');
    }
  };

  const handleShareToCommunity = async () => {
    setShareStatus('sharing');
    try {
      await communityAPI.shareScore(boardId, score);
      setShareStatus('shared');
      toast.success('Board shared to the Community!');
    } catch (error) {
      setShareStatus(null);
      toast.error(error.response?.data?.detail || 'Failed to share');
    }
  };

  const handleCompleteChallenge = async () => {
    try {
      const response = await communityAPI.completeChallenge(boardId, score);
      setChallengeResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record score');
    }
  };

  // Auto-submit challenge score when game ends
  // Record play count when game is completed (all 3 rounds done)
  useEffect(() => {
    if (phase === 'final_results' && !isCommunityChallenge && board) {
      boardsAPI.completeGame(boardId).catch(() => {});
    }
  }, [phase]);

  useEffect(() => {
    if (phase === 'final_results' && isCommunityChallenge && !challengeResult) {
      handleCompleteChallenge();
    }
  }, [phase]);

  const playAgain = () => {
    // Check if board would be expired after this play was recorded
    const currentPlayCount = (board?.play_count || 0) + 1; // +1 because current game just completed
    if (currentPlayCount >= 3) {
      setBoardExpired(true);
      return;
    }
    setScore(0);
    setAllClues([]);
    setRoundClues([]);
    setSelectedClue(null);
    setAnswer('');
    setLastResult(null);
    setIsLastQuestion(false);
    setRound2Ready(false);
    setRound2Loading(false);
    setupRound(1, board);
  };

  const formatRoundTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const BoardHeader = () => (
    <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" className="text-gray-300 shrink-0" onClick={() => navigate(isCommunityChallenge ? '/community' : '/boards')}>
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <img src={LOGO_URL} alt="JepPrep" className="h-10 shrink-0" />
          </div>
          {!['loading', 'final_results'].includes(phase) && (
            <div className="flex items-center gap-2 sm:gap-6 shrink-0">
              <span className="text-[hsl(var(--brand-gold-500))] font-bold text-xs sm:text-sm bg-[hsl(var(--brand-blue-800))] px-2 sm:px-3 py-1 rounded-full">{roundConfig.label}</span>
              <div className="flex items-center gap-1 sm:gap-2 text-gray-400">
                <Target className="w-3 h-3 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm">{answeredCount}/{totalClues}</span>
              </div>
              <div className={`flex items-center gap-1 sm:gap-2 ${roundTimeLeft <= 60 ? 'text-rose-400' : roundTimeLeft <= 180 ? 'text-yellow-400' : 'text-gray-400'}`}>
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" /><span className="font-mono text-xs sm:text-sm">{formatRoundTime(roundTimeLeft)}</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-[hsl(var(--brand-gold-500))]" />
                <span className={`font-mono font-bold text-xs sm:text-base ${score >= 0 ? 'text-[hsl(var(--brand-gold-500))]' : 'text-rose-400'}`}>${score.toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );

  const handleDeleteBoard = async () => {
    try {
      await boardsAPI.delete(boardId);
      toast.success('Board deleted');
      navigate('/boards');
    } catch {
      toast.error('Failed to delete board');
    }
  };

  const handleShareExpiredBoard = async () => {
    try {
      await boardsAPI.publish(boardId);
      toast.success('Board shared to Community!');
      navigate('/community');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to share board');
    }
  };

  // ============ BOARD EXPIRED ============
  if (boardExpired) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-6 sm:p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-orange-400" />
              </div>
              <h1 className="font-display font-bold text-2xl text-white mb-2" data-testid="board-expired-title">Board Expired</h1>
              <p className="text-gray-400 mb-2">{board?.title}</p>
              <p className="text-gray-500 text-sm mb-6">
                You've played this board the maximum 3 times. Would you like to delete it or share it with the community?
              </p>
              <div className="flex flex-col gap-3">
                {!board?.community_challenge && (
                  <Button
                    onClick={handleShareExpiredBoard}
                    className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white w-full py-5"
                    data-testid="share-expired-btn"
                  >
                    <Share2 className="w-5 h-5 mr-2" />
                    Share to Community
                  </Button>
                )}
                <Button
                  onClick={handleDeleteBoard}
                  variant="outline"
                  className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10 w-full py-5"
                  data-testid="delete-expired-btn"
                >
                  <Trash2 className="w-5 h-5 mr-2" />
                  Delete Board
                </Button>
                <Button
                  onClick={() => navigate('/boards')}
                  variant="ghost"
                  className="text-gray-400 hover:text-white w-full"
                  data-testid="back-to-boards-btn"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Back to Boards
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    );
  }

  // ============ LOADING ============
  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[hsl(var(--brand-gold-500))] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading board...</p>
          </div>
        </div>
      </div>
    );
  }

  // ============ ROUND STATS (after R1 or R2) ============
  if (phase === 'round_stats') {
    const nextLabel = currentRound === 1 
      ? (round2Loading ? 'Preparing Round 2...' : 'Round 2') 
      : (board.final_jeopardy ? 'Final Round' : null);
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <StatsDisplay
          answeredClues={roundClues}
          score={score}
          title={`${roundConfig.label} Complete!`}
          subtitle={`Cumulative Score: $${score.toLocaleString()}`}
          onContinue={proceedFromRoundStats}
          continueLabel={nextLabel}
        />
      </div>
    );
  }

  // ============ FINAL RESULTS (after Round 3 / game over) ============
  if (phase === 'final_results') {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <StatsDisplay
          answeredClues={allClues}
          score={score}
          title={isCommunityChallenge ? "Challenge Complete!" : "Game Over!"}
          subtitle={board?.title}
          onPlayAgain={isCommunityChallenge ? null : playAgain}
          onDashboard={() => navigate(isCommunityChallenge ? '/community' : '/dashboard')}
          onShareCommunity={!isCommunityChallenge && !board?.community_challenge ? handleShareToCommunity : null}
          shareStatus={shareStatus}
          challengeResult={challengeResult}
          shareLink={(isCommunityChallenge || shareStatus === 'shared' || board?.community_challenge) ? `${window.location.origin}/community/${boardId}` : null}
        />
      </div>
    );
  }

  // ============ DAILY DOUBLE REVEAL ============
  if (phase === 'dd_reveal') {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <div className="flex-1 flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }} className="text-center">
          <div className="bg-[hsl(var(--brand-blue-800))] rounded-2xl p-12 border-4 border-[hsl(var(--brand-gold-500))]">
            <Star className="w-20 h-20 mx-auto text-[hsl(var(--brand-gold-500))] mb-4" />
            <h1 className="font-display text-5xl font-bold text-[hsl(var(--brand-gold-500))]">DAILY DOUBLE!</h1>
            <p className="text-gray-400 mt-3 text-lg">{selectedClue?.category}</p>
          </div>
        </motion.div>
        </div>
      </div>
    );
  }

  // ============ DAILY DOUBLE WAGER ============
  if (phase === 'dd_wager') {
    const maxWager = Math.max(score, currentRound === 1 ? 500 : 1000);
    const timerPct = (clueTimeLeft / CLUE_TIME) * 100;
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <div className="flex-1 flex items-start md:items-start items-center justify-center p-4 pt-4 md:pt-8">
        <div className="w-full max-w-lg">
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-400 mb-2">
              <span>Daily Double Wager</span>
              <span className="font-mono text-white">{clueTimeLeft}s</span>
            </div>
            <div className="timer-bar"><div className={`timer-bar-fill ${clueTimeLeft <= 2 ? 'danger' : clueTimeLeft <= 5 ? 'warning' : ''}`} style={{ width: `${timerPct}%` }} /></div>
          </div>
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-8 text-center">
              <Star className="w-12 h-12 mx-auto text-[hsl(var(--brand-gold-500))] mb-4" />
              <h2 className="text-white text-2xl font-display font-bold mb-2">How much will you wager?</h2>
              <p className="text-gray-400 mb-2">{selectedClue?.category}</p>
              <p className="text-gray-500 text-sm mb-6">Score: <span className="text-[hsl(var(--brand-gold-500))] font-bold">${score.toLocaleString()}</span> &middot; Max: <span className="text-white font-bold">${maxWager.toLocaleString()}</span></p>
              <form onSubmit={(e) => { e.preventDefault(); handleDDWagerSubmit(); }}>
                <div className="flex gap-3 justify-center">
                  <Input ref={inputRef} type="number" min="0" max={maxWager} placeholder="Enter wager..." value={wager} onChange={(e) => setWager(e.target.value)} className="w-48 bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-lg py-6 text-center" data-testid="dd-wager-input" />
                  <Button type="submit" className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 py-6" data-testid="dd-wager-submit">Wager</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    );
  }

  // ============ FINAL ROUND WAGER (unlimited time) ============
  if (phase === 'final_wager') {
    const maxWager = Math.max(score, 0);
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <Card className="bg-gradient-to-b from-[hsl(var(--brand-blue-800))] to-[hsl(var(--secondary))] border-[hsl(var(--brand-gold-500))]">
            <CardContent className="p-8 text-center">
              <Trophy className="w-12 h-12 mx-auto text-[hsl(var(--brand-gold-500))] mb-4" />
              <h2 className="text-[hsl(var(--brand-gold-500))] text-2xl font-display font-bold mb-2">FINAL ROUND</h2>
              <p className="text-white text-lg mb-2">Category: {board.final_jeopardy.category}</p>
              <p className="text-gray-400 text-sm mb-6">Score: <span className="text-[hsl(var(--brand-gold-500))] font-bold">${score.toLocaleString()}</span></p>
              <form onSubmit={(e) => { e.preventDefault(); submitFinalWager(); }}>
                <div className="flex gap-3 justify-center">
                  <Input ref={inputRef} autoFocus type="number" min="0" max={maxWager} placeholder="Enter wager..." value={wager} onChange={(e) => setWager(e.target.value)} className="w-48 bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-lg py-6 text-center" data-testid="fj-wager-input" />
                  <Button type="submit" className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 py-6" data-testid="fj-wager-submit">Wager</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    );
  }

  // ============ FINAL ROUND CLUE (25s timer) ============
  if (phase === 'final_clue') {
    const timerPct = (clueTimeLeft / FINAL_CLUE_TIME) * 100;
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <div className="flex-1 flex items-start md:items-start items-center justify-center p-4 pt-4 md:pt-8">
        <div className="w-full max-w-2xl">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-400 mb-2">
              <span>Final Round &middot; Wager: ${selectedClue?.wager?.toLocaleString()}</span>
              <span className="font-mono text-white">{clueTimeLeft}s</span>
            </div>
            <div className="timer-bar"><div className={`timer-bar-fill ${clueTimeLeft <= 5 ? 'danger' : clueTimeLeft <= 10 ? 'warning' : ''}`} style={{ width: `${timerPct}%` }} /></div>
          </div>
          <Card className="clue-display mb-6">
            <div className="text-[hsl(var(--brand-gold-500))] text-sm font-semibold mb-2">FINAL ROUND &mdash; {selectedClue?.category}</div>
            <div className="text-white text-xl md:text-2xl" data-testid="fj-question">{selectedClue?.question}</div>
          </Card>
          <form onSubmit={(e) => { e.preventDefault(); submitFinalAnswer(answer); }}>
            <div className="flex gap-3">
              <Input ref={inputRef} type="text" placeholder="Type your answer..." value={answer} onChange={(e) => setAnswer(e.target.value)} autoFocus disabled={isSubmitting} className="flex-1 bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-lg py-6" data-testid="fj-answer-input" />
              <Button type="submit" disabled={isSubmitting || !answer.trim()} className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 py-6" data-testid="fj-submit-btn">Submit</Button>
            </div>
          </form>
        </div>
        </div>
      </div>
    );
  }

  // ============ FEEDBACK SCREENS ============
  if (phase === 'result_feedback' || phase === 'final_feedback') {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <div className="flex-1 flex items-start justify-center p-4 pt-4 md:pt-8">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-lg text-center">
          <Card className={`border-2 ${lastResult?.is_correct ? 'bg-green-500/10 border-green-500' : lastResult?.was_timeout ? 'bg-orange-500/10 border-orange-500' : 'bg-red-500/10 border-red-500'}`}>
            <CardContent className="p-8">
              {lastResult?.is_correct ? <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" /> : lastResult?.was_timeout ? <Clock className="w-16 h-16 mx-auto text-orange-500 mb-4" /> : <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />}
              <h2 className={`text-2xl font-display mb-2 ${lastResult?.is_correct ? 'text-green-500' : lastResult?.was_timeout ? 'text-orange-500' : 'text-red-500'}`}>
                {lastResult?.is_correct ? 'Correct!' : lastResult?.was_timeout ? "Time's Up!" : 'Incorrect'}
              </h2>
              <p className="text-gray-400 mb-1">{lastResult?.is_correct ? '+' : '-'}${Math.abs(lastResult?.score_change || 0).toLocaleString()}</p>
              <p className="text-gray-300">Correct answer:</p>
              <p className="text-white text-xl font-semibold">{lastResult?.correct_answer}</p>
            </CardContent>
          </Card>
        </motion.div>
        </div>
      </div>
    );
  }

  if (phase === 'skip_feedback') {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <div className="flex-1 flex items-start justify-center p-4 pt-4 md:pt-8">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-lg text-center">
          <Card className="border-2 bg-orange-500/10 border-orange-500">
            <CardContent className="p-8">
              <SkipForward className="w-16 h-16 mx-auto text-orange-400 mb-4" />
              <h2 className="text-2xl font-display text-orange-400 mb-2">Skipped</h2>
              <p className="text-gray-400 mb-1">$0 (no deduction)</p>
              <p className="text-gray-300">Correct answer:</p>
              <p className="text-white text-xl font-semibold">{lastResult?.correct_answer}</p>
            </CardContent>
          </Card>
        </motion.div>
        </div>
      </div>
    );
  }

  // ============ CLUE VIEW (Sprint-style) ============
  if (phase === 'clue') {
    const timerPct = (clueTimeLeft / CLUE_TIME) * 100;
    const isDD = selectedClue?.wager !== undefined;
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
        <BoardHeader />
        <div className="flex-1 flex items-start md:items-start items-center justify-center p-4 pt-4 md:pt-8">
        <div className="w-full max-w-2xl">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-400 mb-2">
              <span>{isDD ? `Daily Double \u00b7 Wager: $${selectedClue.wager.toLocaleString()}` : `${selectedClue?.category} \u00b7 $${selectedClue?.value}`}</span>
              <span className="font-mono text-white">{clueTimeLeft}s</span>
            </div>
            <div className="timer-bar"><div className={`timer-bar-fill ${clueTimeLeft <= 2 ? 'danger' : clueTimeLeft <= 5 ? 'warning' : ''}`} style={{ width: `${timerPct}%` }} /></div>
          </div>
          <motion.div key="clue" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="clue-display mb-6">
              <div className="text-[hsl(var(--brand-gold-500))] text-sm font-semibold mb-2">{selectedClue?.category} {isDD && <span className="text-yellow-300 ml-2">DAILY DOUBLE</span>}</div>
              <div className="text-white text-xl md:text-2xl" data-testid="board-question">{selectedClue?.question}</div>
            </Card>
            <form onSubmit={handleFormSubmit}>
              <div className="space-y-3">
                <Input ref={inputRef} type="text" placeholder="Type your answer..." value={answer} onChange={(e) => setAnswer(e.target.value)} autoFocus disabled={isSubmitting} className="w-full bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-lg py-6" data-testid="board-answer-input" />
                <div className="flex gap-3">
                  <Button type="submit" disabled={isSubmitting || !answer.trim()} className="flex-1 bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-base" data-testid="board-submit-button">Submit</Button>
                  {!isDD && (
                    <Button type="button" onClick={handleSkip} disabled={isSubmitting} variant="outline" className="flex-1 border-orange-500/50 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300 py-6 text-base" data-testid="board-skip-button">
                      <SkipForward className="w-5 h-5 mr-2" />Skip
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </motion.div>
        </div>
        </div>
      </div>
    );
  }

  // ============ BOARD GRID ============
  const values = roundConfig.values;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
      <BoardHeader />

      <div className="flex-1 flex items-start justify-center p-2 sm:p-4 pt-4 sm:pt-6">
        <div className="w-full max-w-6xl">
          {/* Mobile: Category tiles or single-category values */}
          <div className="md:hidden"
            onTouchStart={(e) => { if (mobileCatIdx !== null) touchStartRef.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (mobileCatIdx === null || !touchStartRef.current) return;
              const diff = touchStartRef.current - e.changedTouches[0].clientX;
              if (Math.abs(diff) > 50) {
                if (diff > 0 && mobileCatIdx < currentCategories.length - 1) setMobileCatIdx(prev => prev + 1);
                else if (diff < 0 && mobileCatIdx > 0) setMobileCatIdx(prev => prev - 1);
              }
              touchStartRef.current = null;
            }}
          >
            {mobileCatIdx === null ? (
              /* Category tiles grid */
              <div className="grid grid-cols-2 gap-3 px-1" data-testid="mobile-cat-tiles">
                {currentCategories.map((cat, idx) => {
                  const answeredInCat = values.filter((_, rowIdx) => revealed[`${idx}-${rowIdx}`]).length;
                  const totalInCat = cat.clues?.length || 5;
                  const allDone = answeredInCat === totalInCat;
                  return (
                    <button key={cat.name || `cat-${idx}`} onClick={() => setMobileCatIdx(idx)}
                      className={`rounded-xl p-4 text-center transition-all active:scale-95 ${allDone ? 'bg-[hsl(var(--accent))]/60 border border-gray-700' : 'bg-[hsl(var(--brand-blue-800))] border border-[hsl(var(--brand-gold-500))]/20 hover:border-[hsl(var(--brand-gold-500))]/50'}`}
                      data-testid={`cat-tile-${idx}`}
                    >
                      <p className={`font-bold text-sm uppercase leading-tight mb-2 ${allDone ? 'text-gray-500' : 'text-[hsl(var(--brand-gold-500))]'}`}>{cat.name}</p>
                      <p className={`text-xs ${allDone ? 'text-gray-600' : 'text-gray-400'}`}>{answeredInCat}/{totalInCat} answered</p>
                    </button>
                  );
                })}
              </div>
            ) : (
              /* Selected category values */
              <>
                <div className="flex items-center justify-between mb-3 px-1">
                  <Button variant="ghost" size="sm" onClick={() => setMobileCatIdx(null)} className="text-gray-300 shrink-0" data-testid="back-to-tiles">
                    <ChevronLeft className="w-4 h-4 mr-1" />Categories
                  </Button>
                  <p className="text-[hsl(var(--brand-gold-500))] font-bold text-sm uppercase truncate mx-2 text-center flex-1" data-testid="mobile-cat-name">{currentCategories[mobileCatIdx]?.name}</p>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => setMobileCatIdx(Math.max(0, mobileCatIdx - 1))} disabled={mobileCatIdx === 0} className="text-white disabled:text-gray-600 h-8 w-8" data-testid="swipe-prev">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setMobileCatIdx(Math.min(currentCategories.length - 1, mobileCatIdx + 1))} disabled={mobileCatIdx >= currentCategories.length - 1} className="text-white disabled:text-gray-600 h-8 w-8" data-testid="swipe-next">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex justify-center gap-1.5 mb-4">
                  {currentCategories.map((cat, idx) => (
                    <button key={cat.name || `dot-${idx}`} onClick={() => setMobileCatIdx(idx)} className={`h-1.5 rounded-full transition-all ${idx === mobileCatIdx ? 'bg-[hsl(var(--brand-gold-500))] w-5' : 'bg-gray-600 w-1.5'}`} data-testid={`cat-dot-${idx}`} />
                  ))}
                </div>

                <div className="space-y-2">
                  {values.map((val, rowIdx) => {
                    const key = `${mobileCatIdx}-${rowIdx}`;
                    const isRevealed = revealed[key];
                    const clueExists = currentCategories[mobileCatIdx]?.clues?.[rowIdx];
                    return (
                      <button key={key} onClick={() => !isRevealed && clueExists && selectClue(mobileCatIdx, rowIdx)} disabled={isRevealed || !clueExists || roundTimeLeft <= 0}
                        className={`w-full rounded-lg p-5 text-center font-bold text-2xl transition-all ${isRevealed ? 'bg-[hsl(var(--accent))]/50 text-gray-700 cursor-default' : roundTimeLeft <= 0 ? 'bg-[hsl(var(--accent))] text-gray-600 cursor-not-allowed' : 'bg-[hsl(var(--brand-blue-800))] text-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-blue-800))]/80 active:scale-95 cursor-pointer'}`}
                        data-testid={`mobile-cell-${mobileCatIdx}-${rowIdx}`}
                      >
                        {isRevealed ? '' : `$${val}`}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Desktop: Full grid */}
          <div className="hidden md:block">
            <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: `repeat(${currentCategories.length}, 1fr)` }}>
              {currentCategories.map((cat, ci) => (
                <div key={ci} className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 text-center">
                  <p className="text-[hsl(var(--brand-gold-500))] font-bold text-xs sm:text-sm uppercase truncate" data-testid={`cat-header-${ci}`}>{cat.name}</p>
                </div>
              ))}
            </div>

            {values.map((val, rowIdx) => (
              <div key={rowIdx} className="grid gap-2 mb-2" style={{ gridTemplateColumns: `repeat(${currentCategories.length}, 1fr)` }}>
                {currentCategories.map((cat, catIdx) => {
                  const key = `${catIdx}-${rowIdx}`;
                  const isRevealed = revealed[key];
                  const clueExists = cat.clues?.[rowIdx];
                  return (
                    <button key={key} onClick={() => !isRevealed && clueExists && selectClue(catIdx, rowIdx)} disabled={isRevealed || !clueExists || roundTimeLeft <= 0}
                      className={`rounded-lg p-4 text-center font-bold text-lg sm:text-xl transition-all ${isRevealed ? 'bg-[hsl(var(--accent))]/50 text-gray-700 cursor-default' : roundTimeLeft <= 0 ? 'bg-[hsl(var(--accent))] text-gray-600 cursor-not-allowed' : 'bg-[hsl(var(--brand-blue-800))] text-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-blue-800))]/80 hover:scale-105 cursor-pointer active:scale-95'}`}
                      data-testid={`cell-${catIdx}-${rowIdx}`}
                    >
                      {isRevealed ? '' : `$${val}`}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {roundTimeLeft <= 0 && (
            <div className="text-center mt-6">
              <p className="text-rose-400 font-semibold mb-4">Time's up! Round over.</p>
              <Button onClick={() => setPhase('round_stats')} className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-6 py-5" data-testid="go-stats-btn">
                <BarChart3 className="w-5 h-5 mr-2" />View {roundConfig.label} Stats
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
